/**
 * TSL materials for the spike test room.
 *
 * Rule for this folder: plain functions that return three.js node materials.
 * No React here, so everything ports unchanged to a vanilla three.js
 * Experience class if G1 picks the escape hatch over R3F v10.
 */
import * as THREE from 'three/webgpu'
import {
  Fn,
  color,
  float,
  min,
  mix,
  mx_fractal_noise_float,
  mx_noise_float,
  positionLocal,
  positionWorld,
  smoothstep,
  step,
  time,
  uv,
  vec2,
  vec3,
} from 'three/tsl'
import { palette } from './palette'
import { setBloomMask } from './bloomMask'

/** Rain-dark stone floor: noise-driven puddles that go glossy and dark. */
export function createWetStoneMaterial() {
  const m = new THREE.MeshPhysicalNodeMaterial()

  const p = positionWorld.xz
  const puddle = smoothstep(0.02, 0.22, mx_fractal_noise_float(vec3(p.mul(0.3), 0), 3, 2.0, 0.5))
  const grit = mx_noise_float(vec3(p.mul(16.0), 1.7)).mul(0.5).add(0.5)

  m.colorNode = mix(color(palette.stone).mul(grit.mul(0.5).add(0.75)), color('#030304'), puddle)
  m.roughnessNode = mix(float(0.72).add(grit.mul(0.18)), float(0.035), puddle)
  m.metalnessNode = float(0)
  m.clearcoatNode = puddle
  m.clearcoatRoughnessNode = float(0.02)
  return m
}

/** Matte black slab body for monoliths. */
export function createMonolithBodyMaterial() {
  const m = new THREE.MeshStandardNodeMaterial()
  m.colorNode = color('#060607')
  m.roughnessNode = float(0.55)
  m.metalnessNode = float(0.2)
  return m
}

/**
 * Stand-in for an artwork until real works and depth maps arrive (week 2).
 * Self-lit, slowly drifting crimson/blue field with a thin bone frame.
 * Emissive goes above 1.0 (like a bright real artwork would after exposure),
 * which is exactly why the art is masked out of bloom below.
 */
export function createPlaceholderArtMaterial(seed: number) {
  const m = new THREE.MeshStandardNodeMaterial()

  const st = uv()
  const edge = min(min(st.x, st.y), min(st.x.oneMinus(), st.y.oneMinus()))
  const frame = step(edge, 0.01)

  const art = Fn(() => {
    const field = mx_fractal_noise_float(
      vec3(st.mul(vec2(2.2, 3.4)), time.mul(0.04).add(seed * 7.31)),
      4,
      2.0,
      0.5,
    )
    const band = smoothstep(-0.25, 0.55, field.add(st.y.sub(0.5).mul(0.8)))
    const base = mix(color(palette.blue).mul(0.6), color(palette.crimson).mul(1.6), band)
    return mix(base, color(palette.bone).mul(1.2), frame)
  })()

  m.colorNode = color('#000000')
  m.emissiveNode = art
  m.roughnessNode = float(0.4)
  // The artwork itself never blooms (true colour, black void); its thin frame may.
  setBloomMask(m, frame)
  return m
}

/** Lacquered crimson for the torii stand-in. */
export function createLacquerMaterial() {
  const m = new THREE.MeshPhysicalNodeMaterial()
  m.colorNode = color(palette.crimson).mul(0.55)
  m.roughnessNode = float(0.35)
  m.clearcoatNode = float(0.8)
  m.clearcoatRoughnessNode = float(0.15)
  return m
}

/**
 * Stress pillars: dark shaft with one teal emissive band whose height
 * varies per instance. Cheap, but enough to load fill-rate and vertex work.
 */
export function createPillarMaterial() {
  const m = new THREE.MeshStandardNodeMaterial()
  const y = positionLocal.y
  const band = smoothstep(0.35, 0.38, y).mul(step(y, 0.42))
  m.colorNode = color('#08090b')
  m.roughnessNode = float(0.6)
  m.emissiveNode = color(palette.teal).mul(band.mul(2.5))
  return m
}
